import React, { useState } from 'react';
import { Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';

// Import colors and fonts from existing design system
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  black: '#000000',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 500,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const CredentialLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { loginWithEmail } = useAuth();

  const handleBack = () => {
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      console.log('Login attempt:', { email, password: '***' });
      
      // Use the AuthContext loginWithEmail function
      const user = await loginWithEmail(email, password);
      
      console.log('✅ Login successful:', user);

      toast.success('Welcome back!');
      
      // The AuthContext will handle setting needsOnboarding
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Login error:', err);
      
      // Handle specific error cases
      if (err.response?.data?.requiresGoogle) {
        setError('This account uses Google login only. Please sign in with Google.');
      } else if (err.response?.status === 401) {
        setError('Invalid email or password. Please check your credentials.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div
      className="min-h-screen bg-white flex items-center justify-center px-6 py-12"
      style={{
        fontFamily: fontStyles.fontFamily,
        backgroundColor: colors.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '3rem 1.5rem',
      }}
    >
      <div
        className="w-full max-w-md mx-auto"
        style={{
          width: '100%',
          maxWidth: '400px',
          margin: '0 auto',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          style={{ 
            color: colors.gray700,
            transition: 'color 0.2s ease'
          }}
        >
          <ArrowLeft size={20} className="mr-2" />
          Back
        </button>

        {/* Header Section */}
        <div className="text-center mb-6">
          {/* Main Heading with inline logo */}
          <h1
            className="text-4xl font-bold mb-4 flex items-center justify-center"
            style={{
              color: colors.black,
              ...fontStyles.heading,
              lineHeight: '1.1',
            }}
          >
            Get started with
            <img src="/6.png" alt="SignPact Logo" className="h-12 ml-2" style={{marginTop: '4px'}} />
          </h1>

          
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div
              className="p-3 rounded-lg text-sm"
              style={{
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                border: '1px solid #fecaca',
              }}
            >
              {error}
            </div>
          )}

          {/* Email Input */}
          <div className="space-y-2">
            <label 
              className="block text-sm font-medium" 
              style={{ color: colors.gray700 }}
            >
              Email
            </label>
            <div 
              className="flex items-center border rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all" 
              style={{ 
                borderColor: colors.gray200, 
                backgroundColor: colors.gray50 
              }}
            >
              <Mail size={18} className="ml-4 mr-2" style={{ color: colors.gray500 }} />
              <input
                type="email"
                className="w-full px-2 py-3 bg-transparent focus:outline-none"
                style={{ color: colors.gray900 }}
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Password Input */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label 
                className="block text-sm font-medium" 
                style={{ color: colors.gray700 }}
              >
                Password
              </label>
              <Link
                to="/reset-password"
                className="text-sm font-medium hover:underline"
                style={{ color: colors.accent }}
              >
                Forgot Password?
              </Link>
            </div>
            <div 
              className="flex items-center border rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all" 
              style={{ 
                borderColor: colors.gray200, 
                backgroundColor: colors.gray50 
              }}
            >
              <Lock size={18} className="ml-4 mr-2" style={{ color: colors.gray500 }} />
              <input
                type={showPassword ? 'text' : 'password'}
                className="w-full px-2 py-3 bg-transparent focus:outline-none"
                style={{ color: colors.gray900 }}
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 transition-colors"
                style={{ color: colors.gray500 }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Sign In Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 px-6 rounded-xl font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: colors.accent,
              transition: 'all 0.2s ease',
              ...fontStyles.medium,
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = colors.accentHover;
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.backgroundColor = colors.accent;
              }
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Footer Section */}
        <div className="text-center mt-8">
          <p
            className="text-sm"
            style={{ color: colors.gray700 }}
          >
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="font-medium hover:underline"
              style={{ color: colors.accent }}
            >
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CredentialLoginPage; 